import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Calendar, Clock, CheckCircle, XCircle, LogOut, Award, AlertCircle } from 'lucide-react';
import axios from 'axios';

const EventCheckIn = () => {
  const { eventId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [event, setEvent] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    fetchEventDetails();
    requestLocation();
  }, [eventId]);

  const requestLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          setLocationError(error.message);
          console.error('Location error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser');
    }
  };

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch event
      const eventRes = await axios.get(`${backendUrl}/api/events/${eventId}`);
      setEvent(eventRes.data);
      
      // Fetch registration
      const registrationsRes = await axios.get(
        `${backendUrl}/api/events/my-registrations`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      const userReg = registrationsRes.data.find(reg => reg.event_id === eventId);
      if (userReg) {
        setRegistration(userReg);
        
        // Fetch QR code
        const qrRes = await axios.get(
          `${backendUrl}/api/events/registrations/${userReg.id}/qr`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setQrCode(qrRes.data);
        
        // Fetch check-in status
        const statusRes = await axios.get(
          `${backendUrl}/api/events/${eventId}/checkin/status`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setCheckInStatus(statusRes.data);
      }
      
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load event details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!location) {
      toast({
        title: 'Location Required',
        description: 'Please enable location services to check in',
        variant: 'destructive'
      });
      requestLocation();
      return;
    }

    if (!qrCode) {
      toast({
        title: 'QR Code Error',
        description: 'QR code not found. Please try refreshing.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setProcessing(true);
      
      const response = await axios.post(
        `${backendUrl}/api/events/${eventId}/checkin`,
        {
          qr_code_data: qrCode.qr_code_data,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast({
        title: 'Check-In Successful!',
        description: `You checked in at ${new Date(response.data.checkin_timestamp).toLocaleTimeString()}`,
      });

      // Refresh status
      fetchEventDetails();
      
    } catch (error) {
      console.error('Check-in error:', error);
      const errorMsg = error.response?.data?.detail || 'Check-in failed';
      toast({
        title: 'Check-In Failed',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    if (!location) {
      toast({
        title: 'Location Required',
        description: 'Please enable location services to check out',
        variant: 'destructive'
      });
      requestLocation();
      return;
    }

    try {
      setProcessing(true);
      
      const response = await axios.post(
        `${backendUrl}/api/events/${eventId}/checkout`,
        {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast({
        title: 'Check-Out Successful!',
        description: `Duration: ${Math.floor(response.data.attendance_duration_minutes)} minutes`,
      });

      // Refresh status
      fetchEventDetails();
      
    } catch (error) {
      console.error('Check-out error:', error);
      const errorMsg = error.response?.data?.detail || 'Check-out failed';
      toast({
        title: 'Check-Out Failed',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHero title="Event Check-In" />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHero title="Event Check-In" />
        <div className="container mx-auto px-4 py-12 max-w-2xl">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Not Registered</h3>
              <p className="text-gray-600 mb-4">You are not registered for this event.</p>
              <Button onClick={() => navigate('/events')}>Browse Events</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="Event Check-In" />
      
      <div className="container mx-auto px-4 py-8 md:py-12 max-w-2xl">
        {/* Event Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{event?.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <Calendar className="mr-2 h-4 w-4" />
              <span>{new Date(event?.event_date).toLocaleDateString('en-ZA', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            
            {event?.event_time && (
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="mr-2 h-4 w-4" />
                <span>{event.event_time}</span>
              </div>
            )}
            
            {event?.location && (
              <div className="flex items-center text-sm text-gray-600">
                <MapPin className="mr-2 h-4 w-4" />
                <span>{event.location}</span>
              </div>
            )}
            
            {event?.cpd_points && (
              <div className="flex items-center text-sm text-gray-600">
                <Award className="mr-2 h-4 w-4" />
                <span>{event.cpd_points} CPD Points</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location Status */}
        {locationError ? (
          <Card className="mb-6 border-orange-300 bg-orange-50">
            <CardContent className="py-4">
              <div className="flex items-center text-orange-800">
                <AlertCircle className="mr-2 h-5 w-5" />
                <div>
                  <p className="font-medium">Location Access Required</p>
                  <p className="text-sm">{locationError}</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2"
                    onClick={requestLocation}
                  >
                    Enable Location
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : location && (
          <Card className="mb-6 border-green-300 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center text-green-800">
                <CheckCircle className="mr-2 h-5 w-5" />
                <div>
                  <p className="font-medium">Location Enabled</p>
                  <p className="text-sm">Accuracy: ±{Math.round(location.accuracy)}m</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-In Status Card */}
        {checkInStatus && !checkInStatus.has_checked_in && (
          <Card className="border-2 border-[#2c3e7d]">
            <CardHeader>
              <CardTitle className="text-center">Ready to Check In</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-gray-600">
                Click the button below to check in to this event
              </p>
              
              <Button 
                onClick={handleCheckIn}
                disabled={processing || !location}
                className="w-full bg-[#2c3e7d] hover:bg-[#1e2a5a] text-lg py-6"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Checking In...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Check In Now
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {checkInStatus && checkInStatus.has_checked_in && !checkInStatus.has_checked_out && (
          <Card className="border-2 border-green-600">
            <CardHeader>
              <CardTitle className="text-center text-green-600">
                <CheckCircle className="inline mr-2 h-6 w-6" />
                Checked In
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <p className="text-gray-600 mb-2">Check-in time:</p>
                <p className="text-lg font-semibold">
                  {new Date(checkInStatus.checkin_timestamp).toLocaleTimeString()}
                </p>
              </div>
              
              <div className="pt-4 border-t">
                <Button 
                  onClick={handleCheckOut}
                  disabled={processing || !location}
                  variant="outline"
                  className="w-full text-lg py-6 border-2 border-red-600 text-red-600 hover:bg-red-50"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-2"></div>
                      Checking Out...
                    </>
                  ) : (
                    <>
                      <LogOut className="mr-2 h-5 w-5" />
                      Check Out
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {checkInStatus && checkInStatus.has_checked_out && (
          <Card className="border-2 border-blue-600">
            <CardHeader>
              <CardTitle className="text-center text-blue-600">
                Event Completed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Check-in:</span>
                  <span className="font-semibold">
                    {new Date(checkInStatus.checkin_timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Check-out:</span>
                  <span className="font-semibold">
                    {new Date(checkInStatus.checkout_timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold">
                    {Math.floor(checkInStatus.attendance_duration_minutes)} minutes
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Attendance:</span>
                  <span className="font-semibold">
                    {checkInStatus.attendance_percentage?.toFixed(0)}%
                  </span>
                </div>
              </div>

              {checkInStatus.cpd_eligible && (
                <div className="pt-4 border-t bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                  <div className="flex items-center justify-center text-green-800">
                    <Award className="mr-2 h-5 w-5" />
                    <span className="font-semibold">
                      Eligible for {event?.cpd_points} CPD Points
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/my-events')}
          >
            Back to My Events
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventCheckIn;
